import { toConsumableArray as _toConsumableArray } from '../_virtual/_rollupPluginBabelHelpers.js';
import { usePathContext } from '../context/PathContext.js';
import { PathGraphFlowRunMode } from '../generated/types.js';
import handleRequestEvents from '../helpers/handleRequestEvents.js';
import deriveNodeNavigationDetails from '../helpers/deriveNodeNavigationDetails.js';
import deriveOnCompleteActions from '../helpers/deriveOnCompleteActions.js';
import isPathOnTerminalNode from '../helpers/isPathOnTerminalNode.js';

function useHandleNextNode(startAtEnd) {
  var pathContext = usePathContext();
  return function (_ref) {
    var pathGraphFlow = _ref.pathGraphFlow,
        directedActiveNodeSlugOrId = _ref.directedActiveNodeSlugOrId,
        onProgressToNextNode = _ref.onProgressToNextNode;

    /**
     * Reset the context, but always reference the prop so there aren't race conditions
     */
    pathContext.onSetPathGraphFlow(pathGraphFlow);
    pathContext.onSetRunMode(pathGraphFlow.runMode);
    /**
     * Use a local copy of the runMode to avoid async issues
     */

    var pathRunMode = pathGraphFlow.runMode;
    /**
     * In the event there are any errors on the path graph flow, don't attempt to move to the next node
     */

    if (pathContext.pathGraphFlowErrors) {
      return;
    }

    var currentNodeIndex = pathGraphFlow.nodes.findIndex(function (node) {
      var _a;

      return node.id === ((_a = pathContext.activeNode) === null || _a === void 0 ? void 0 : _a.id);
    });
    /**
     * Allow the option to start at the end of the flow (returning to visit for the first time) or
     * to just move to the next node. The logic is shared here so we can handle complete and error
     * cases.
     *
     * We also favor a `directedActiveNodeSlugOrId` that comes from outside of the path-flow library.
     * This is external state management of the active flow's location. If it's absent, fall back to
     * selecting the most recent for the given flow OR the next in the sequence if a current node index
     * exists.
     */

    var latestNode;

    if (directedActiveNodeSlugOrId) {
      latestNode = pathGraphFlow === null || pathGraphFlow === void 0 ? void 0 : pathGraphFlow.nodes.find(function (node) {
        var nodeDetails = deriveNodeNavigationDetails(node);

        if (nodeDetails.nodeSlug) {
          return nodeDetails.nodeSlug === directedActiveNodeSlugOrId;
        } else {
          return nodeDetails.nodeId === directedActiveNodeSlugOrId;
        }
      });
    }
    /**
     * In the event we didn't find a node via the `directedActiveNodeSlugOrId` or
     * or it was never defined, fallback to popping off the last one from the nodes array.
     */


    if (!latestNode) {
      latestNode = startAtEnd ? _toConsumableArray(pathGraphFlow === null || pathGraphFlow === void 0 ? void 0 : pathGraphFlow.nodes).pop() : _toConsumableArray(pathGraphFlow === null || pathGraphFlow === void 0 ? void 0 : pathGraphFlow.nodes)[currentNodeIndex + 1];
    }

    var onLastNode = latestNode && isPathOnTerminalNode(pathGraphFlow, latestNode.id);
    /**
     * Check to see if there were any issues submitting our current node
     */

    var currentNodeInResponse = pathGraphFlow.nodes[currentNodeIndex];
    var validationErrors = [];

    if (currentNodeInResponse) {
      switch (currentNodeInResponse.viewModel.__typename) {
        /**
         * We currently only check the `QuestionsPathNodeFlowViewModel` for errors. The only errors that
         * we surface have an error message with no action. If those errors get more complex, we can expand
         * the type of the `validationErrors` in context to be more complex.
         */
        case 'QuestionsPathNodeFlowViewModel':
          currentNodeInResponse.viewModel.questions.forEach(function (question) {
            return question.commonFields.validationErrors.map(function (validationError) {
              validationErrors.push(validationError);
            });
          });
          break;
      }
      /**
       * If there are any erorrs, render them and don't try to advance forward
       */


      if (validationErrors.length > 0) {
        pathContext.onSetNodeLevelErrors(validationErrors);
        return;
      }
    }
    /**
     * Path's with a run mode of`PathGraphFlowRunMode.DRY_RUN` cannot complete in the traditional sense.
     * If it's a dry run path and there is latest node, simply navigate.
     */


    if (pathRunMode === PathGraphFlowRunMode.DRY_RUN) {
      latestNode && pathContext.onSetActiveNode(latestNode);
      return;
    }
    /**
     * Check if any request events were returned in the mutation response. As this expands, we should relocate
     * this to an helper outside of the `handleNextNode` method.
     */


    if (pathGraphFlow.requestEvents.length > 0) {
      handleRequestEvents(pathGraphFlow.requestEvents, pathGraphFlow.id);
    }

    if (pathGraphFlow.completed && onLastNode && latestNode) {
      /**
       * If the path is completed and there are no more nodes,
       * execute path handlers to redirect, close modal, etc.
       *
       * Pass the terminal node back out of path flow so the
       * consuming applications can interpret it.
       */
      pathContext.onPathCompleted(Object.assign({
        pathNavigationDetails: {
          nodeSlugOrId: latestNode.id,
          pathGraphFlowId: pathGraphFlow.id,
          pathGraphFlowSlug: latestNode.pathGraphSlug
        },
        requestEvents: pathGraphFlow.requestEvents
      }, deriveOnCompleteActions(latestNode)));
      return;
    } else if (latestNode) {
      pathContext.onSetActiveNode(latestNode);

      if (onProgressToNextNode) {
        onProgressToNextNode();
      }

      return;
    }

    if (!pathGraphFlow.completed) {
      throw new Error("Path is not completed but there are no remaining nodes, [".concat(pathGraphFlow.id, "]"));
    }
  };
}

export default useHandleNextNode;
//# sourceMappingURL=useHandleNextNode.js.map
